import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { io } from 'socket.io-client';
import DialerInterface from '../components/dialer/DialerInterface';
import ContactList from '../components/dialer/ContactList';
import CallHistory from '../components/dialer/CallHistory';
import CampaignSelector from '../components/dialer/CampaignSelector';
import RealTimeDashboard from '../components/dialer/RealTimeDashboard';
import AgentStatusManager from '../components/dialer/AgentStatusManager';
import AgentPerformanceDashboard from '../components/dialer/AgentPerformanceDashboard';
import ComplianceDashboard from '../components/dialer/ComplianceDashboard';
import TeamChat from '../components/dialer/TeamChat';
import KnowledgeBase from '../components/dialer/KnowledgeBase';
import UserPreferences from '../components/dialer/UserPreferences';
import WebhookManager from '../components/dialer/WebhookManager';
import TeamManager from '../components/dialer/TeamManager';
import SchedulingManager from '../components/dialer/SchedulingManager';
import Leaderboard from '../components/dialer/Leaderboard';
import WorkflowBuilder from '../components/dialer/WorkflowBuilder';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Dialer() {
  const { user } = useAuth();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [showManagerView, setShowManagerView] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    connectWebSocket();
    fetchAgentId();
    
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  const fetchAgentId = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/agents/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && response.data.id) {
        setAgentId(response.data.id);
      }
    } catch (error) {
      console.error('Error fetching agent ID:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE}/dialer/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data);
      if (response.data.length > 0 && !selectedCampaign) {
        setSelectedCampaign(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    const token = localStorage.getItem('accessToken');
    // Use relative path for WebSocket - will use same origin
    const wsUrl = import.meta.env.VITE_API_URL || window.location.origin;
    const newSocket = io(wsUrl, {
      auth: { token },
      transports: ['websocket']
    });

    newSocket.on('connect', () => {
      console.log('Connected to dialer WebSocket');
      if (selectedCampaign) {
        newSocket.emit('dialer:join', {
          campaignId: selectedCampaign.id
        });
      }
    });

    newSocket.on('call:initiated', (data) => {
      console.log('Call initiated', data);
      setActiveCall(data);
      setCallStatus('initiated');
    });

    newSocket.on('call:ringing', (data) => {
      console.log('Call ringing', data);
      setCallStatus('ringing');
    });

    newSocket.on('call:answered', (data) => {
      console.log('Call answered', data);
      setCallStatus('answered');
    });

    newSocket.on('call:ended', (data) => {
      console.log('Call ended', data);
      setActiveCall(null);
      setCallStatus(null);
    });

    newSocket.on('call:update', (data) => {
      console.log('Call update', data);
      if (data.callId === activeCall?.id) {
        setCallStatus(data.status);
      }
    });

    setSocket(newSocket);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading dialer...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">Phone Dialer</h1>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowManagerView(!showManagerView)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                {showManagerView ? 'Agent View' : 'Manager View'}
              </button>
              <CampaignSelector
                campaigns={campaigns}
                selectedCampaign={selectedCampaign}
                onSelect={setSelectedCampaign}
                onCreateNew={fetchCampaigns}
              />
            </div>
          </div>

      {selectedCampaign ? (
        <div>
          {showManagerView ? (
            <div className="space-y-6">
              <RealTimeDashboard />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ComplianceDashboard campaignId={selectedCampaign.id} />
                {agentId && (
                  <AgentPerformanceDashboard 
                    agentId={agentId}
                    startDate={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()}
                    endDate={new Date().toISOString()}
                  />
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TeamChat teamId={1} />
                <KnowledgeBase />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TeamManager />
                <WebhookManager />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Leaderboard />
                <WorkflowBuilder />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {agentId && <AgentStatusManager agentId={agentId} />}
                <DialerInterface
                  campaign={selectedCampaign}
                  activeCall={activeCall}
                  callStatus={callStatus}
                  socket={socket}
                  onCallInitiated={setActiveCall}
                  onCallEnded={() => {
                    setActiveCall(null);
                    setCallStatus(null);
                  }}
                />
              </div>
              <div className="space-y-6">
                <ContactList campaignId={selectedCampaign.id} />
                <CallHistory campaignId={selectedCampaign.id} />
                {agentId && <SchedulingManager agentId={agentId} />}
                <UserPreferences />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">Please select or create a campaign to start dialing</p>
        </div>
      )}
    </div>
  );
}

